import { useMutation } from '@apollo/client';
import {
  faCheckCircle,
  faExclamationCircle,
  faHourglass,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import type { DropzoneProps, FileRejection } from 'react-dropzone';
import Dropzone from 'react-dropzone';
import { FormProvider, useFieldArray } from 'react-hook-form';
import { toast } from 'react-toastify';
import invariant from 'tiny-invariant';
import { v4 as uuid } from 'uuid';
import { z } from 'zod';
import { graphql } from '~/apollo/generated/v4';
import { DropzoneContainer } from '~/components/common/DropzoneContainer';
import { SpinnerIcon } from '~/components/common/SpinnerIcon';
import { Button } from '~/components/ui/button';
import { HookFormErrors } from '~/components/ui/forms/HookFormErrors';
import { TextInput } from '~/components/ui/forms/text-input';
import { useRefetchQueriesV4 } from '~/hooks/apollo';
import { cn } from '~/utils/common';
import { useZodForm } from '~/utils/forms';
import type { RefetchQueries } from '~/utils/graphql';
import { uploadFile } from '~/utils/modules/file';
import { fileSizeText } from '~/utils/text';
import { ppStringOrNull } from '~/utils/zod';

const itemStatuses = ['pending', 'uploading', 'success', 'error'] as const;
type ItemStatus = (typeof itemStatuses)[number];

const schema = z.object({
  items: z.array(
    z.object({
      tmpId: z.string(),
      file: z.instanceof(File),
      status: z.enum(itemStatuses),
      values: z.object({
        note: z.preprocess(ppStringOrNull, z.string().nullish()),
      }),
    }),
  ),
});

type FormValues = {
  items: {
    tmpId: string;
    file: File;
    values: { note: string };
    status: ItemStatus;
  }[];
};

const initialValues = (): FormValues => ({
  items: [],
});

const CREATE_SUPPLEMENTAL_DOCUMENT = graphql(`
  mutation CreateSupplementalDocument(
    $input: SupplementalDocumentCreateInput!
  ) {
    supplementalDocumentCreate(input: $input) {
      token
      path
    }
  }
`);

export function SupplementalDocumentUploader({
  outcropId,
  studyId,
  refetchQueries,
}: {
  outcropId?: number;
  studyId?: number;
  refetchQueries: RefetchQueries;
}) {
  invariant(outcropId || studyId, 'outcropId or studyId required');

  const [createSupDoc] = useMutation(CREATE_SUPPLEMENTAL_DOCUMENT);
  const [refetch] = useRefetchQueriesV4(refetchQueries);
  const [isUploading, setIsUploading] = useState(false);

  const form = useZodForm({
    schema,
    values: initialValues(),
  });
  const fieldArray = useFieldArray({
    control: form.control,
    name: 'items',
  });

  const queueItems = fieldArray.fields;
  const pendingItems = queueItems.filter(item => item.status === 'pending');
  const errorItems = queueItems.filter(item => item.status === 'error');
  const hasPendingItems = pendingItems.length > 0;

  const handleDrop: DropzoneProps['onDrop'] = acceptedFiles => {
    acceptedFiles.forEach(file =>
      fieldArray.append({
        tmpId: uuid(),
        file,
        status: 'pending',
        values: { note: '' },
      }),
    );
  };

  const handleRemove = (index: number) => () => {
    fieldArray.remove(index);
  };

  const setItemStatus = (index: number, status: ItemStatus) => {
    fieldArray.update(index, { ...fieldArray.fields[index], status });
  };

  async function uploadItem(
    file: File,
    values: z.infer<typeof schema>['items'][number],
  ) {
    const { tmpId, ...input } = values;

    try {
      const createRes = await createSupDoc({
        variables: {
          input: {
            supplementalDocument: {
              outcropId,
              studyId,
              ...input.values,
            },
          },
        },
      });

      const token = createRes.data?.supplementalDocumentCreate.token;
      const path = createRes.data?.supplementalDocumentCreate.path;
      invariant(token && path, 'supplementalDocumentCreate failed');

      return uploadFile({ path, token, file });
    } catch (err) {
      console.error('Error uploading file:', err);
      return false;
    }
  }

  const handleSubmit = form.handleSubmit(async values => {
    setIsUploading(true);

    const pendingItems = values.items.filter(
      item => item.status === 'pending' || item.status === 'error',
    );

    for (const item of pendingItems) {
      const index = values.items.findIndex(i => i.tmpId === item.tmpId);
      setItemStatus(index, 'uploading');
      const isSuccess = await uploadItem(item.file, item);
      setItemStatus(index, isSuccess ? 'success' : 'error');
    }

    await refetch();
    setIsUploading(false);
  });

  function handleDropRejected(rejects: FileRejection[]) {
    const names = rejects.map(r => r.file.name).join('\n');
    toast.error(`The following files weren't accepted:\n${names}`);
  }

  function clearAll() {
    if (!window.confirm('Are you sure you want to clear the queue?')) return;
    fieldArray.remove(queueItems.map((_, i) => i));
  }

  const isQueueLocked = isUploading;

  const isItemLocked = (item: (typeof queueItems)[number]) =>
    isQueueLocked || item.status !== 'pending';

  return (
    <div className="space-y-6">
      <Dropzone
        onDrop={handleDrop}
        onDropRejected={handleDropRejected}
        multiple
        disabled={isQueueLocked}
        maxSize={100_000_000}
      >
        {({ getRootProps, getInputProps }) => (
          <DropzoneContainer {...getRootProps()}>
            <div
              className={cn('flex items-center', {
                'h-16': queueItems.length,
                'h-96': !queueItems.length,
              })}
              style={{
                transition: 'height 0.5s',
              }}
            >
              <input {...getInputProps()} />
              {isQueueLocked ? (
                <p>Uploading...</p>
              ) : (
                <p>Drop files here or click to browse</p>
              )}
            </div>
          </DropzoneContainer>
        )}
      </Dropzone>

      <FormProvider {...form}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            {queueItems.map((item, index) => (
              <div
                key={item.tmpId}
                className=" grid grid-cols-12 gap-4 border border-slate-200 p-2"
              >
                <div className="flex w-full h-full items-center justify-center space-y-2">
                  <div className="text-center pt-2 space-y-2">
                    <ItemStatusIcon status={item.status} />
                    <Button
                      type="button"
                      color="ghost"
                      size="sm"
                      disabled={isItemLocked(item)}
                      onClick={handleRemove(index)}
                    >
                      Remove
                    </Button>
                  </div>
                </div>

                <div className="col-span-11 flex justify-between gap-6">
                  <div className="grow space-y-2">
                    <div className="font-bold text-base">
                      {item.file.name}{' '}
                      <span className="text-sm text-muted">
                        {fileSizeText(item.file.size)}
                      </span>
                    </div>

                    <TextInput
                      {...form.register(`items.${index}.values.note`)}
                      label="Note"
                      disabled={isItemLocked(item)}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <HookFormErrors />

          <div className="text-center space-x-1">
            <Button
              type="button"
              color="ghost"
              onClick={clearAll}
              disabled={isUploading || !queueItems.length}
            >
              Clear All
            </Button>

            <Button
              type="submit"
              color="primary"
              disabled={(!hasPendingItems && !errorItems.length) || isUploading}
              loading={isUploading}
            >
              Upload
            </Button>
          </div>
        </form>
      </FormProvider>
    </div>
  );
}

function ItemStatusIcon({ status }: { status: ItemStatus }) {
  switch (status) {
    case 'pending':
      return (
        <FontAwesomeIcon
          icon={faHourglass}
          className=" text-slate-200 text-2xl"
        />
      );
    case 'error':
      return (
        <FontAwesomeIcon
          icon={faExclamationCircle}
          className="text-error text-2xl"
        />
      );
    case 'success':
      return (
        <FontAwesomeIcon
          icon={faCheckCircle}
          className="text-success text-2xl"
        />
      );
    case 'uploading':
      return <SpinnerIcon className="text-2xl" />;
  }
}
